"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

import { Route } from "@/enums/route";
import { useInactivityEnabled } from "@/hooks/useInactivityEnabled";
import { useLogout } from "@/hooks/useLogout";
import { trackEvent } from "@/lib/analytics";
import { isTestEnv } from "@/lib/env";
import {
  createInactivityClock,
  electInactivityLeader,
  type InactivityClock,
  type InactivityRole,
} from "@/lib/inactivityClock";
import { logError } from "@/lib/logError";
import { ACCESS_TOKEN_EXPIRING_NOTIFICATION_SECONDS } from "@/providers/AuthProvider/userManagerSingleton";
import { runWithCleanup } from "@/utils/runWithCleanup";

/** How often the render-only countdown re-reads the clock's time left. */
const COUNTDOWN_TICK_MS = 500;

/** Async, so a synchronous throw from `removeUser` arrives as a rejection. */
const dropLocalToken = async (
  removeUser: (() => Promise<void>) | undefined
) => {
  await removeUser?.();
};

export interface UseIdleLogoutResult {
  /** Whether the "Are you still here?" warning is showing. */
  isPrompted: boolean;
  /** Whole seconds left before the hard logout (drives the visible countdown). */
  remainingSeconds: number;
  /** Total warning-window length in whole seconds (the countdown ring's denominator). */
  totalSeconds: number;
  /** "Stay signed in" — restarts the clock and clears the prompt. */
  staySignedIn: () => void;
  /** "Sign out now" — immediate inactivity logout. */
  signOutNow: () => void;
}

/**
 * Inactivity logout on the shared clock in `lib/inactivityClock.ts`: one
 * last-activity stamp in `localStorage` for every tab of the origin, so the
 * app is idle only when every tab is idle (design §8), and a tab whose own
 * timer went quiet — a cancelled unload, a throttled background tab — re-reads
 * the stamp on its next wake instead of timing out while another tab is in use.
 *
 * The warning opens at stamp + idle-before-warning and the logout lands at
 * stamp + idle-before-warning + the fixed 60s countdown (the countdown sits ON
 * TOP of the idle period). The default 4:00 idle + 60s = 5:00 total is the
 * SCA-RTS ceiling (design §3/§4), and the clock holds it with no deferral
 * slack: a hidden tab in the countdown sleeps to the logout instant and takes
 * it only after the leader election and a final read of the stamp.
 *
 * The countdown is render-only, derived from the clock's time left (no second
 * clock to drift). The clock runs only while the enabled predicate holds
 * (design §10), so it binds no events and never logs out when signed out / in
 * the test env.
 */
export function useIdleLogout(): UseIdleLogoutResult {
  const auth = useAuth();
  const logout = useLogout();

  // The enabled predicate and the env-derived config (fixed for the life of
  // the mount) come from the shared hook the crash fallback also gates on.
  const {
    enabled,
    config: { timing },
  } = useInactivityEnabled();

  const [isPrompted, setIsPrompted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.ceil(timing.promptMs / 1000)
  );

  // Refs so the clock callbacks keep a stable identity and never go stale.
  // Synced in a post-render effect below (updating refs during render trips
  // the react-hooks/refs rule and is read-after-render by the callbacks anyway).
  const logoutRef = useRef(logout);
  const authRef = useRef(auth);
  const removeUserRef = useRef(auth.removeUser);
  const timeoutMsRef = useRef(timing.timeoutMs);
  const promptMsRef = useRef(timing.promptMs);
  const clockRef = useRef<InactivityClock | null>(null);

  const handlePrompt = useCallback(() => {
    // Re-arm the countdown on the OPEN edge, batched into the same commit as
    // the open, so the dialog's first painted frame already shows a full
    // window. It deliberately does NOT re-arm on close (see the countdown
    // effect below).
    setRemainingSeconds(Math.ceil(promptMsRef.current / 1000));
    setIsPrompted(true);
  }, []);

  const handleActive = useCallback(() => {
    setIsPrompted(false);
  }, []);

  const handleExpire = useCallback((role: InactivityRole) => {
    setIsPrompted(false);

    if (role === "leader") {
      // Leader tab owns the authoritative sign-out (ends the Keycloak SSO
      // session + the silent-failure reporting) and emits the funnel event
      // once. trackEvent is a no-op in test env per the analytics convention.
      if (!isTestEnv) {
        trackEvent("Inactivity Logout", {
          reason: "idle",
          idle_ms: timeoutMsRef.current,
        });
      }
      logoutRef.current({ redirectTo: Route.Dashboard, reason: "inactivity" });
      return;
    }

    // Follower tabs just drop the local token and hard-navigate to "/" so
    // the auth gate forces re-login. They do NOT call signoutRedirect —
    // that would fire N redundant end-session hits and N false silent-
    // failure reports for a session the leader already ended (design §8).
    //
    // AWAIT the token teardown BEFORE navigating: removeUser() is async, and
    // navigating while it is still in flight could let the stale local token
    // survive the reload, allowing the follower to silently re-authenticate
    // against the still-valid Keycloak SSO cookie — the exact thing the
    // force-re-auth flow prevents (design §6). runWithCleanup guarantees the
    // navigation still happens even if removeUser rejects (a try/finally here
    // would switch the React Compiler lint rules off for this hook).
    const removeUser = removeUserRef.current;
    void runWithCleanup(
      () =>
        dropLocalToken(removeUser).catch((error: unknown) => {
          // The leader already ended the SSO session; a failed local-token
          // drop here must not block (or unhandled-reject out of) the
          // follower's navigation. Log for traceability; the cleanup then
          // runs the reload.
          logError(
            "[useIdleLogout] Follower removeUser failed during idle logout",
            error
          );
        }),
      () => {
        if (typeof window !== "undefined") {
          window.location.assign(Route.Dashboard);
        }
      }
    );
  }, []);

  // Sync every ref after render (declared before the clock and countdown
  // effects so it runs first in the commit).
  useEffect(() => {
    logoutRef.current = logout;
    authRef.current = auth;
    removeUserRef.current = auth.removeUser;
    timeoutMsRef.current = timing.timeoutMs;
    promptMsRef.current = timing.promptMs;
  });

  // One clock per enabled mount. `timing` is fixed for the life of the mount
  // and the handlers are stable, so this re-runs only when `enabled` flips.
  useEffect(() => {
    if (!enabled) return;
    const clock = createInactivityClock({
      timing,
      onWarn: handlePrompt,
      onActive: handleActive,
      onExpire: handleExpire,
      // Through the module binding, so the election stays replaceable.
      electLeader: () => electInactivityLeader(),
    });
    clockRef.current = clock;
    clock.start();
    return () => {
      clock.stop();
      if (clockRef.current === clock) clockRef.current = null;
      setIsPrompted(false);
    };
  }, [enabled, timing, handlePrompt, handleActive, handleExpire]);

  // Render-only countdown: while prompted, re-read the clock's time left and
  // surface whole seconds. No second authoritative clock — the clock owns time.
  //
  // On close the last value is left FROZEN on purpose (the interval stops, the
  // number stays put). Radix keeps AlertDialogContent mounted for its 200ms
  // fade/zoom-out, so re-arming here would make the user watch the number snap
  // back to the full window — and with it the ring re-fill over its 1s tween
  // and the polite live region announce "60 seconds" — as the dialog they just
  // dismissed fades away. The re-arm lives on the open edge (`handlePrompt`).
  useEffect(() => {
    if (!isPrompted) return;

    const tick = () => {
      const remainingMs =
        clockRef.current?.getRemainingMs() ?? promptMsRef.current;
      setRemainingSeconds(Math.max(0, Math.ceil(remainingMs / 1000)));
    };

    tick();
    const intervalId = setInterval(tick, COUNTDOWN_TICK_MS);
    return () => clearInterval(intervalId);
  }, [isPrompted]);

  const staySignedIn = useCallback(() => {
    // Moves the shared stamp to now (the one thing that does once the warning
    // is due) and clears the prompt in every tab.
    clockRef.current?.staySignedIn();
    // "Stay signed in" is an explicit liveness signal — make sure the OIDC
    // session can actually honour it. If the access token lapsed (or is
    // inside the pre-expiry renewal window) while the warning sat open, kick
    // a coalesced silent renewal now so the user doesn't click "stay" only
    // to be bounced by a dead token moments later. No-ops for a fresh token
    // and in E2E (the mock auth context has no real user).
    const user = authRef.current.user;
    if (
      user &&
      (user.expired ||
        (user.expires_at ?? 0) * 1000 - Date.now() <
          ACCESS_TOKEN_EXPIRING_NOTIFICATION_SECONDS * 1000)
    ) {
      void authRef.current.signinSilent().catch(() => {
        // Failure surfaces via the auth layer (silentRenewError / redirect).
      });
    }
  }, []);

  const signOutNow = useCallback(() => {
    logoutRef.current({ redirectTo: Route.Dashboard, reason: "inactivity" });
  }, []);

  // The warning window is fixed for the life of the mount; surface it in whole
  // seconds so the dialog's countdown ring has a stable denominator.
  const totalSeconds = Math.ceil(timing.promptMs / 1000);

  return useMemo(
    () => ({
      isPrompted,
      remainingSeconds,
      totalSeconds,
      staySignedIn,
      signOutNow,
    }),
    [isPrompted, remainingSeconds, totalSeconds, staySignedIn, signOutNow]
  );
}
